﻿import React, { useEffect, useState, Suspense, lazy } from "react";
import { Routes, Route, Navigate, Outlet, useLocation, useNavigate, useParams } from "react-router-dom";
import { MODULES, LEGACY_REDIRECTS, findSection, sectionPath } from "./components/admin/adminModules";
import { Toaster } from "react-hot-toast";
import CookieConsent from "./components/CookieConsent";
import SetPasswordPrompt from "./components/onboarding/SetPasswordPrompt";
import { isNative } from "./lib/platform";
import { useSessionTimeout } from "./utils/sessionTimeout";
import { UserTypeProvider, CompanyRoute, ContractorRoute } from './components/UserTypeGate';
import { apiUrl } from "./lib/api";
// Legal gate
import AgreementGate from "./components/legal/AgreementGate";
import ScrollToTop from "./components/ScrollToTop";
import { initAnalytics, metaTrack, gaTrack, consumeStripeIntent, captureAttribution } from "./lib/analytics";
import { initTracker, trackEvent } from "./lib/tracker";
import { rehydrateExperimentContext, homepageArm } from "./lib/experiment";

// Eagerly-loaded shells. Layouts + providers run on every route, so
// lazy-loading them would just add an extra round trip without saving
// any bytes. Marketing landing pieces (HeroSection) and the login page
// stay eager too â€” ad clicks land there, and rendering a Suspense
// fallback before the hero would look worse than the current behavior.
import AuthLayout from "./layouts/AuthLayout";
import HeroLanding from "./layouts/HeroLanding";
import DashboardLayout from "./layouts/DashboardLayout";
import BusinessLanding from "./pages/BusinessLanding";
import TalentLanding from "./pages/TalentLanding";
import MarketHome from "./pages/MarketHome";
import { MarketShell } from "./components/MarketHeader";
import PendingMessageBanner from "./components/PendingMessageBanner";
import LoginPage from "./pages/LoginPage";

// Context providers
import { PermissionProvider } from "./contexts/PermissionContext";
import { NotificationProvider } from "./contexts/NotificationContext";
import { BrandingProvider, useBranding } from "./contexts/BrandingContext";

// Page visit tracker â€” fires a lightweight beacon on every route change
import usePageVisitTracker from "./hooks/usePageVisitTracker";

// Protected page wrappers â€” small, used across many routes
import { ProtectedTeamPage, ProtectedOrgManagementPage, ProtectedIncomeStatementPage, ProtectedStripePage, ProtectedBillingPage, ProtectedFraudAlertsPage } from "./components/company/ProtectedPages";

// =============================================================================
// Lazy-loaded routes
// =============================================================================
// Each non-shell page is code-split so the initial bundle only contains
// the marketing landing + auth + shared shells. Before this change every
// dashboard, profile editor, and admin page rode in on first load, which
// is why public marketing pages felt slow. Suspense's fallback is left
// empty (`null`) so router transitions look like instant nav rather than
// a flash of a spinner â€” the browser still shows the previous route
// until the chunk lands.

// Public pages
const RegisterPage = lazy(() => import("./pages/RegisterPage"));
const VerifyEmailPage = lazy(() => import("./pages/VerifyEmailPage"));
const VerifyEmailTokenPage = lazy(() => import("./pages/VerifyEmailTokenPage"));
const AppWelcome = lazy(() => import("./pages/AppWelcome"));
const PartnerApplyPage = lazy(() => import("./pages/partners/PartnerApplyPage"));
const PartnerDashboardPage = lazy(() => import("./pages/partners/PartnerDashboardPage"));
const PartnerApplyPublicPage = lazy(() => import("./pages/partners/PartnerApplyPublicPage"));
const About = lazy(() => import("./pages/About"));
const ContactPage = lazy(() => import("./pages/ContactPage"));
const OrganizationRegisterPage = lazy(() => import("./pages/OrganizationRegisterPage"));
const OrgStorefrontPage = lazy(() => import("./pages/OrgStorefrontPage"));
const ServicesVerticalPage = lazy(() => import("./pages/services/ServicesVerticalPage"));
const ServicesOfferingPage = lazy(() => import("./pages/services/ServicesOfferingPage"));
const ServicesFractionalCatalogPage = lazy(() => import("./pages/services/ServicesFractionalCatalogPage"));
const ServicesFractionalRolePage = lazy(() => import("./pages/services/ServicesFractionalRolePage"));
const ContractorPublicProfilePage = lazy(() => import("./pages/ContractorPublicProfilePage"));
const CompanyInboundRequestsPage = lazy(() => import("./pages/company/CompanyInboundRequestsPage"));
const CompanyPostingsPage = lazy(() => import("./pages/company/CompanyPostingsPage"));
const CompanyPostingBoardPage = lazy(() => import("./pages/company/CompanyPostingBoardPage"));
const CompanyStorefrontEditorPage = lazy(() => import("./pages/company/CompanyStorefrontEditorPage"));
const ContractorPostingsPage = lazy(() => import("./pages/contractor/ContractorPostingsPage"));
const YourCardPage = lazy(() => import("./pages/YourCardPage"));
const ApplicationReviewPage = lazy(() => import("./pages/contractor/ApplicationReviewPage"));
const ContractorInboxPage = lazy(() => import("./pages/contractor/ContractorInboxPage"));
const ContractorStorefrontEditorPage = lazy(() => import("./pages/contractor/ContractorStorefrontEditorPage"));
const PublicRequestTrackerPage = lazy(() => import("./pages/PublicRequestTrackerPage"));
const PublicRateRequestPage = lazy(() => import("./pages/PublicRateRequestPage"));
const MyRequestsPage = lazy(() => import("./pages/MyRequestsPage"));
const PublicPostingDetailPage = lazy(() => import("./pages/postings/PublicPostingDetailPage"));
const HomePage = lazy(() => import("./pages/HomePage"));
const MessagesPage = lazy(() => import("./pages/MessagesPage"));
const QuestionSetsPage = lazy(() => import("./pages/company/QuestionSetsPage"));
const NotFoundPage = lazy(() => import("./pages/NotFoundPage"));
const BlogPage = lazy(() => import("./pages/BlogPage"));
const BlogPostPage = lazy(() => import("./pages/BlogPostPage"));
const PartnerProgramPage = lazy(() => import("./pages/partners/PartnerProgramPage"));
const PrivacyPolicyPage = lazy(() => import("./pages/PrivacyPolicyPage"));
const DeleteAccountPage = lazy(() => import("./pages/DeleteAccountPage"));
const AccountDeletedPage = lazy(() => import("./pages/AccountDeletedPage"));
const SecurityPage = lazy(() => import("./pages/SecurityPage"));
const SavingsCalculatorPage = lazy(() => import("./pages/SavingsCalculatorPage"));
const CalendlyConfirmationPage = lazy(() => import("./pages/CalendlyConfirmationPage"));
const AccountSecurityPage = lazy(() => import("./pages/AccountSecurityPage"));
const TermsPage = lazy(() => import("./pages/TermsPage"));
const LegalAgreementPage = lazy(() => import("./pages/LegalAgreementPage"));
const DMCAPage = lazy(() => import("./pages/DMCAPage"));
const AccessibilityPage = lazy(() => import("./pages/AccessibilityPage"));
const SEOLandingPage = lazy(() => import("./pages/SEOLandingPage"));

// Public (no auth) pages
const PublicOneTimeContractPage = lazy(() => import("./pages/PublicOneTimeContractPage"));
const PublicSignDocumentPage = lazy(() => import("./pages/PublicSignDocumentPage"));
const PublicPaymentConfirmPage = lazy(() => import("./pages/PublicPaymentConfirmPage"));
const PublicPaymentSuccessPage = lazy(() => import("./pages/PublicPaymentSuccessPage"));

// Customer-side dashboard (gates aggregation/analytics behind sign-in;
// individual contracts stay accessible via tokenized link)
const CustomerDashboardPage = lazy(() => import("./pages/customer/CustomerDashboardPage"));

// Magic-link customer portal (no auth) â€” cross-contract identity for
// off-platform customers who don't want to create an account.
const CustomerPortalRequestPage = lazy(() => import("./pages/customer/CustomerPortalRequestPage"));
const CustomerPortalPage = lazy(() => import("./pages/customer/CustomerPortalPage"));

// Authenticated standalone pages (no shared layout)
const ClockInPage = lazy(() => import("./pages/ClockInPage"));
const ClockInCredentialsPage = lazy(() => import("./pages/ClockInCredentialsPage"));

// Company pages
const CompanyDashboardPage = lazy(() => import("./pages/company/CompanyDashboardPage"));
const GetStartedPreview = lazy(() => import("./pages/GetStartedPreview"));
const WorkforceCenterPage = lazy(() => import("./pages/company/WorkforceCenterPage"));
const CompanyCatalogPage = lazy(() => import("./pages/company/CompanyCatalogPage"));
const OrgManagementPage = lazy(() => import("./pages/company/OrgManagementPage"));
const CompanyFinancePage = lazy(() => import("./pages/company/CompanyFinancePage"));
const CompanyContractsPage = lazy(() => import("./pages/company/CompanyContractsPage"));
const CompanyInternalProjectsPage = lazy(() => import("./pages/company/CompanyInternalProjectsPage"));
const CompanyPayRunsPage = lazy(() => import("./pages/company/CompanyPayRunsPage"));
const CompanyPayRunPage = lazy(() => import("./pages/company/CompanyPayRunPage"));
const CompanyInternalProjectDetailPage = lazy(() => import("./pages/company/CompanyInternalProjectDetailPage"));
const CompanyContractArchivePage = lazy(() => import("./pages/company/CompanyContractArchivePage"));
const NewContractFromTemplate = lazy(() => import("./pages/company/NewContractFromTemplate"));
const CompanyHirePage = lazy(() => import("./pages/company/CompanyHirePage"));
const CompanyOnboardingPage = lazy(() => import("./pages/company/CompanyOnboardingPage"));
const TransactionHistoryPage = lazy(() => import("./pages/company/TransactionHistoryPage"));
const ContractSignPage = lazy(() => import("./pages/company/CompanyContractSignPage"));
const CompanySubscribePage = lazy(() => import("./pages/company/CompanySubscribePage"));
const FraudAlerts = lazy(() => import("./components/FraudAlerts"));
const FraudAppeal = lazy(() => import("./components/FraudAppeal"));
const FraudTrendChat = lazy(() => import("./components/FraudTrendChart"));
const AdminDisputeDashboard = lazy(() => import("./components/AdminDisputeDashboard"));
const PlatformAdminDashboard = lazy(() => import("./pages/admin/PlatformAdminDashboard"));
const MarketingDashboard = lazy(() => import("./pages/admin/MarketingDashboard"));
const FinanceDashboard = lazy(() => import("./pages/admin/FinanceDashboard"));
const CreamLabelDashboard = lazy(() => import("./pages/admin/CreamLabelDashboard"));
const LaborIndexDashboard = lazy(() => import("./pages/admin/LaborIndexDashboard"));
const GrowthFunnelDashboard = lazy(() => import("./pages/admin/GrowthFunnelDashboard"));
const ClickstreamDashboard = lazy(() => import("./pages/admin/ClickstreamDashboard"));
const KpiTrackerPage = lazy(() => import("./pages/admin/KpiTrackerPage"));
const VinnieDashboard = lazy(() => import("./pages/admin/VinnieDashboard"));
const AdminSupportInboxPage = lazy(() => import("./pages/admin/AdminSupportInboxPage"));
const AdminSupportAnswersPage = lazy(() => import("./pages/admin/AdminSupportAnswersPage"));
const AdminSupportTeamPage = lazy(() => import("./pages/admin/AdminSupportTeamPage"));
const AdminPrivacyRequestsPage = lazy(() => import("./pages/admin/AdminPrivacyRequestsPage"));
const AdminToday = lazy(() => import("./pages/admin/AdminToday"));
const AdminAsksPage = lazy(() => import("./pages/admin/AdminAsksPage"));
const AdminAgreementsPage = lazy(() => import("./pages/admin/AdminAgreementsPage"));
const AdminTalentPage = lazy(() => import("./pages/admin/AdminTalentPage"));
const AdminJobOutreachPage = lazy(() => import("./pages/admin/AdminJobOutreachPage"));
const AdminCallsPage = lazy(() => import("./pages/admin/AdminCallsPage"));
const AdminMemberUpdatePage = lazy(() => import("./pages/admin/AdminMemberUpdatePage"));
const CompanyTravelApprovalsPage = lazy(() => import("./pages/company/CompanyTravelCenterPage"));
const CompanyTravelCenterPage = lazy(() => import("./pages/company/CompanyTravelCenterPage"));
const CompanyFinanceIncomePage = lazy(() => import("./pages/company/CompanyFinanceIncomePage"));
// ARCHIVED 2026-06-11: Talent Search retired (postings + storefront replace it; AI/Ollama cost). Restore route below to re-enable.
// const CompanyTalentSearchPage = lazy(() => import("./pages/company/CompanyTalentSearchPage"));
const CompanyTimecardsPage = lazy(() => import("./pages/company/CompanyTimecardsPage"));
const CompanySignatureDocumentsPage = lazy(() => import("./pages/company/CompanySignatureDocumentsPage"));
const CompanyNoticesPage = lazy(() => import("./pages/company/CompanyNoticesPage"));
const CompanyAgreementsPage = lazy(() => import("./pages/company/CompanyAgreementsPage"));
const CompanyPartnerProgramPage = lazy(() => import("./pages/company/CompanyPartnerProgramPage"));
// Cross-role: a notice goes to everybody an org counts as its roster, which
// includes W-2 employees and back-office members, not only 1099 contractors.
// So it lives under /dashboard, next to my-requests, not under /contractor.
const MyNoticesPage = lazy(() => import("./pages/MyNoticesPage"));
const CompanyTimeOffPage = lazy(() => import("./pages/company/CompanyTimeOffPage"));
// ARCHIVED 2026-06-11: Messaging Center retired. Restore route below to re-enable.
// const CompanyMessagingCenterPage = lazy(() => import("./pages/company/CompanyMessageCenter"));
const CompanyTeamPage = lazy(() => import("./pages/company/CompanyTeamPage"));
const CompanyConnectStripePage = lazy(() => import("./pages/company/ConnectCompanyStripePage"));
const CompanyProfilePage = lazy(() => import("./pages/company/CompanyProfilePage"));
const CompanyMilestonesPage = lazy(() => import("./pages/company/CompanyMilestonePage"));
const CompanyContractPrintPage = lazy(() => import("./pages/company/CompanyContractPrintPage"));
const CompanyPOCenterPage = lazy(() => import("./components/company/CompanyPOCenterPage"));
const CompanyCredentialCenterPage = lazy(() => import("./pages/company/CompanyCredentialCenterPage"));
const ContractPOSettings = lazy(() => import("./components/contracts/ContractPOSettings"));
// ARCHIVED 2026-06-11: Business Card retired (storefront replaces it). Restore route below to re-enable.
// const CompanyCardEditor = lazy(() => import("./pages/company/CompanyCardEditor"));
const OrgTeamCalendarPage = lazy(() => import("./pages/company/OrgTeamCalendarPage"));

// Contractor pages
const DashboardIndexRedirect = lazy(() => import("./pages/DashboardIndexRedirect"));
const ContractorDashboard = lazy(() => import("./pages/contractor/ContractorDashboard"));
const FinishSetupPage = lazy(() => import("./pages/contractor/FinishSetupPage"));
const SocialAuthLanding = lazy(() => import("./pages/SocialAuthLanding"));
const ContractorContractCenterPage = lazy(() => import("./pages/contractor/ContractorContractCenterPage"));
const ContractorDocumentsToSignPage = lazy(() => import("./pages/contractor/ContractorDocumentsToSignPage"));
// ARCHIVED 2026-06-11: Messaging Center retired. Restore route below to re-enable.
// const ContractorMessagingCenterPage = lazy(() => import("./pages/contractor/ContractorMessagingCenter"));
const ContractorTaxCenterPage = lazy(() => import("./pages/contractor/ContractorTaxCenterPage"));
const ContractorAccountingCenterPage = lazy(() => import("./pages/contractor/ContractorAccountingCenterPage"));
const JobCalendarPage = lazy(() => import("./pages/contractor/JobCalendarPage"));
const ContractorProfileEdit = lazy(() => import("./pages/contractor/ContractorProfileEdit"));
const ComplianceUploadPage = lazy(() => import("./pages/compliance/ComplianceUploadPage"));
const ContractorTravelCenterPage = lazy(() => import("./pages/contractor/ContractorTravelCenterPage"));
const ContractorTravelStatusPage = lazy(() => import("./pages/contractor/ContractorTravelStatusPage"));
const ContractorTravelRequestPage = lazy(() => import("./pages/contractor/ContractorTravelRequestPage"));
const TravelConfirmationPage = lazy(() => import("./pages/contractor/TravelConfirmationPage"));
const ContractorTimecardsPage = lazy(() => import("./pages/contractor/ContractorTimeCardsPage"));
const ContractorMilestonesPage = lazy(() => import("./pages/contractor/ContractorMilestonesPage"));
const MyEarnings = lazy(() => import("./pages/contractor/MyEarningsPage"));
const ContractorStripeSetupPage = lazy(() => import("./pages/contractor/ContractorStripePage"));
const ContractorSupportBot = lazy(() => import("./pages/contractor/ContractorSupportBot"));
const ContractorCredentialsPanel = lazy(() => import("./pages/contractor/ContractorCredentialsPanel"));
const CalendarSyncSettingsPage = lazy(() => import("./pages/contractor/CalendarSyncSettingsPage"));
const ContractorProfilePage = lazy(() => import("./pages/contractor/ContractorProfilePage"));
const ContractorContractPrintPage = lazy(() => import("./pages/contractor/ContractorContractPrintPage"));
const MyAssignmentsPage = lazy(() => import("./pages/contractor/ContractorAssignmentPage"));
// ARCHIVED 2026-06-11: Business Card retired (storefront replaces it). Restore route below to re-enable.
// const BusinessCardEditor = lazy(() => import("./pages/contractor/ContractorCardEditor"));

// const TalentDiscoveryTab = lazy(() => import("./components/TalentDiscoverytab"));

// Other public
const HistoryServices = lazy(() => import("./pages/personal_services/HistoryServices"));
const ServicesLandingPage = lazy(() => import("./pages/personal_services/ServicesLandingPage"));

// Auth helpers
const ForgotPasswordPage = lazy(() => import("./pages/ForgotPasswordPage"));
const ForgotPasswordSentPage = lazy(() => import("./pages/ForgotPasswordSentPage"));
const ResetPasswordPage = lazy(() => import("./pages/ResetPasswordPage"));
const InviteAcceptPage = lazy(() => import("./pages/InviteAcceptPage"));
const JoinTeamPage = lazy(() => import("./pages/JoinTeamPage"));
const ConfirmContractsPage = lazy(() => import("./pages/ConfirmContractsPage"));
const ResetDonePage = lazy(() => import("./pages/ResetDonePage"));
const EmailVerifiedPage = lazy(() => import("./pages/EmailVerifiedPage"));




  // ============================================================================
// Payment Complete â€” auto-closes the Stripe checkout tab
// ============================================================================
function PaymentComplete() {
  const params = new URLSearchParams(window.location.search);
  const cancelled = params.get("cancelled");
  const sessionId = params.get("session_id");
  const [status, setStatus] = React.useState(cancelled ? "cancelled" : "verifying");

  React.useEffect(() => {
    if (!sessionId || cancelled) {
      // No session to verify â€” just close
      const t = setTimeout(() => window.close(), 2000);
      return () => clearTimeout(t);
    }

    // Verify payment with our backend (marks timecard as paid)
    fetch(apiUrl("/api/contracts/timecards/verify-payment/"), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ session_id: sessionId }),
    })
      .then((r) => r.json())
      .then((data) => {
        setStatus(data.paid ? "paid" : "pending");
        setTimeout(() => window.close(), 2000);
      })
      .catch(() => {
        setStatus("error");
        setTimeout(() => window.close(), 3000);
      });
  }, [sessionId, cancelled]);

  const messages = {
    verifying: { title: "Verifying payment...", sub: "Please wait" },
    paid: { title: "Payment Successful!", sub: "This tab will close automatically..." },
    pending: { title: "Payment Pending", sub: "Your payment is being processed. You can close this tab." },
    cancelled: { title: "Payment Cancelled", sub: "This tab will close automatically..." },
    error: { title: "Could not verify payment", sub: "Check the timecards page. This tab will close shortly." },
  };
  const msg = messages[status] || messages.verifying;

  return (
    <div className="flex items-center justify-center min-h-screen bg-bg text-ink">
      <div className="text-center space-y-4">
        <div className="w-8 h-8 border-2 rounded-full animate-spin mx-auto" style={{ borderColor: "var(--brand)", borderTopColor: "transparent" }} />
        <h1 className="text-2xl font-semibold">{msg.title}</h1>
        <p className="text-secondary">{msg.sub}</p>
      </div>
    </div>
  );
}

// ============================================================================
// Auth Guard Component
// ============================================================================
// Root entry. On the web this is the marketing landing page (SEO +
// conversion). In the native app we must NOT open to that page: Apple
// Guideline 4.2 flags apps that launch into a website-style marketing
// page. So inside Capacitor we route logged-in users to the hub and
// logged-out users to the sign-in screen (which leads with Sign in with
// Apple), landing the reviewer in real app functionality, not a homepage.
function SmartHome() {
  const branding = useBranding();
  if (isNative()) {
    const hasToken =
      localStorage.getItem("access") ||
      localStorage.getItem("token") ||
      localStorage.getItem("authToken");
    // Signed in: the hub. Fresh install: ask which side they are on, rather
    // than opening a sign-in form at somebody who has no account yet. See
    // AppWelcome -- it is real app functionality, not a marketing page, so it
    // satisfies 4.2 the same way the login screen did.
    return <Navigate to={hasToken ? "/home" : "/welcome-to-sift"} replace />;
  }
  // Since 2026-10-31 a 50/50 split between the business page and the
  // marketplace (HomeSplit, below). The business page stays at /employers for
  // ads either way. Partner cream-label canvases keep the business page.
  if (branding.branded) return <BusinessLanding />;
  return <HomeSplit />;
}

// The homepage split (lib/experiment.js, HOMEPAGE_EXPERIMENT): half of
// visitors get the business page, half the marketplace, and keep it.
function HomeSplit() {
  const [arm] = useState(homepageArm);
  useEffect(() => {
    trackEvent("homepage_shown", { arm });
  }, [arm]);
  return arm === "market" ? <MarketHome /> : <BusinessLanding />;
}

function RequireAuth({ children }) {
  const hasToken =
    localStorage.getItem("access") ||
    localStorage.getItem("token") ||
    localStorage.getItem("authToken");

  if (!hasToken) {
    return <Navigate to="/auth/login" replace />;
  }

  // Somebody who just opened a team invite is asked for their name and a
  // password on whichever screen they landed on, the clock included.
  return (
    <>
      {children}
      <SetPasswordPrompt />
    </>
  );
}

// ============================================================================
// Private Shell - Wraps all authenticated routes with ToS gate
// ============================================================================
function PrivateShell() {
  return (
    <RequireAuth>
      <AgreementGate slug="tos">
        <PermissionProvider>
          <NotificationProvider>
            <UserTypeProvider>
              <Outlet />
            </UserTypeProvider>
          </NotificationProvider>
        </PermissionProvider>
      </AgreementGate>
    </RequireAuth>
  );
}

// ---------------------------------------------------------------------------
// Admin sections. Every one is an existing screen; this is where each lives.
// ---------------------------------------------------------------------------
const FINANCE_SECTIONS = new Set(["overview", "pnl", "revenue", "expenses", "burn"]);

function AdminSection() {
  const { pathname } = useLocation();
  const { mod, section } = findSection(pathname);
  if (!mod || !section) return <Navigate to="/admin" replace />;
  const s = section.key;
  // Keyed by path so moving between two sections of the same screen starts it
  // fresh instead of carrying the last section's half-loaded state across.
  const k = pathname;
  switch (mod.key) {
    case "numbers":
      if (s === "scoreboard") return <VinnieDashboard key={k} />;
      if (s === "weekly") return <KpiTrackerPage key={k} />;
      if (s === "funnels") return <GrowthFunnelDashboard key={k} />;
      if (s === "asks") return <AdminAsksPage key={k} />;
      return <ClickstreamDashboard key={k} />;
    case "people":
      if (s === "partners") return <CreamLabelDashboard key={k} />;
      if (s === "agreements") return <AdminAgreementsPage key={k} />;
      if (s === "talent") return <AdminTalentPage key={k} />;
      return <PlatformAdminDashboard key={k} section={s} />;
    case "trust":
      if (s === "privacy") return <AdminPrivacyRequestsPage key={k} />;
      return <PlatformAdminDashboard key={k} section={s} />;
    case "inbox":
      if (s === "support") return <AdminSupportInboxPage key={k} />;
      if (s === "answers") return <AdminSupportAnswersPage key={k} />;
      if (s === "team") return <AdminSupportTeamPage key={k} />;
      return <PlatformAdminDashboard key={k} section={s} />;
    case "marketing":
      if (s === "job-outreach") return <AdminJobOutreachPage key={k} />;
      if (s === "calls") return <AdminCallsPage key={k} />;
      if (s === "member-update") return <AdminMemberUpdatePage key={k} />;
      return <MarketingDashboard key={k} section={s} />;
    case "money":
      if (FINANCE_SECTIONS.has(s)) return <FinanceDashboard key={k} section={s} />;
      if (s === "labor-index") return <LaborIndexDashboard key={k} />;
      return <PlatformAdminDashboard key={k} section={s} />;
    default:
      return <Navigate to="/admin" replace />;
  }
}

function AdminModuleRedirect() {
  const { module: m } = useParams();
  const mod = MODULES.find((x) => x.key === m && x.sections.length);
  return <Navigate to={mod ? sectionPath(mod.key, mod.sections[0].key) : "/admin"} replace />;
}

// /admin/marketing?tab=hiring&hiring=12 -> /admin/marketing/hiring?hiring=12.
// The hiring-feature and spotlight alert emails link this way.
function LegacyMarketingRedirect() {
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  const tab = params.get("tab") || "dashboard";
  params.delete("tab");
  const rest = params.toString();
  return <Navigate to={`/admin/marketing/${tab === "dashboard" ? "pipeline" : tab}${rest ? `?${rest}` : ""}`} replace />;
}

// ============================================================================
// Main App Component
// ============================================================================
export default function App() {
  useSessionTimeout();
  usePageVisitTracker();

  // Honor stored cookie consent on every mount. If accepted: loads GA.
  // If declined / no decision / GPC signal: GA stays disabled.
  useEffect(() => { initAnalytics(); }, []);

  // First-party clickstream: bind the global click listener + flush-on-exit.
  // Page views are fired per-route by usePageVisitTracker above. Inert when
  // the visitor has declined cookie consent. Re-establish the A/B variant tag
  // for already-assigned visitors so their events stay attributable post-reload.
  useEffect(() => { initTracker(); rehydrateExperimentContext(); }, []);

  // Capture ad-campaign attribution (?fbclid, ?utm_*, etc.) on every URL
  // change. Idempotent: only fires when the URL actually has the params,
  // merges into existing localStorage cache otherwise.
  const _attributionLocation = useLocation();
  useEffect(() => { captureAttribution(); }, [_attributionLocation.search]);

  // Detect Stripe Checkout returns and fire Meta conversion events with the
  // value the user committed to at click time (stashed via markStripeIntent).
  // Three return signatures are in play across our backend:
  //   ?subscribed=1  - subscription activation (fires Subscribe + Purchase)
  //   ?paid=true     - timecard payment (fires Purchase)
  //   ?success=true  - milestone or flat-fee payment (fires Purchase)
  //
  // We only fire when there's a tracked intent so that refreshing the page
  // after the fact doesn't re-fire the conversion. Server-side CAPI from
  // the Stripe webhook is the source of truth for exact amounts; this
  // pixel-side firing is best-effort for the browsers where it lands.
  //
  // ?subscribed=1 is owned by us â€” we strip it. ?paid=true and ?success=true
  // are read by destination pages for their own UI, so we leave them in place.
  const stripeReturnLocation = useLocation();
  const stripeReturnNavigate = useNavigate();
  useEffect(() => {
    const params = new URLSearchParams(stripeReturnLocation.search);
    const isSubscribeReturn = params.get("subscribed") === "1";
    const isPaymentReturn = params.get("paid") === "true" || params.get("success") === "true";
    if (!isSubscribeReturn && !isPaymentReturn) return;

    const intent = consumeStripeIntent();
    if (intent) {
      const value = typeof intent.value === "number" ? intent.value : 0;
      if (isSubscribeReturn || intent.type === "subscribe") {
        metaTrack("Subscribe", { currency: "USD", value });
        // GA4 has no standard "subscribe" event; use a custom name plus
        // a standard purchase so subscription revenue rolls up alongside
        // one-time payments in GA4's monetization reports.
        gaTrack("subscribe", { currency: "USD", value });
      }
      metaTrack("Purchase", { currency: "USD", value });
      gaTrack("purchase", {
        currency: "USD",
        value,
        transaction_id: `stripe_return_${Date.now()}`,
      });
    }

    if (isSubscribeReturn) {
      params.delete("subscribed");
      const newSearch = params.toString();
      stripeReturnNavigate(
        `${stripeReturnLocation.pathname}${newSearch ? `?${newSearch}` : ""}`,
        { replace: true }
      );
    }
  }, [stripeReturnLocation.pathname, stripeReturnLocation.search, stripeReturnNavigate]);

  return (
    <BrandingProvider>
    <div className="relative min-h-screen bg-black text-white">
      <ScrollToTop />
      <PendingMessageBanner />
      {/* Cookie notice â€” web only. The native apps load no trackers and set no
          tracking cookies, so there is nothing to consent to and showing a
          cookie prompt would (per Apple 5.1.2) imply tracking without ATT. */}
      {!isNative() && <CookieConsent />}
      {/* Toast notifications */}
      <Toaster
        position="top-center"
        toastOptions={{
          duration: 4000,
          style: {
            background: '#1a1a1a',
            color: '#fff',
            border: '1px solid #333',
          },
        }}
      />

      {/* Suspense fallback is null so route transitions feel instant â€”
          the previous route stays on screen until the next chunk lands,
          which on a fast connection is sub-100ms. A visible spinner
          here would actually make the app feel SLOWER for the common
          cached case. */}
      <Suspense fallback={null}>
      <Routes>
        {/* ============================================================
            PUBLIC NO-LAYOUT ROUTES - Standalone signing/payment pages
            ============================================================ */}
        <Route path="/sign/:token" element={<PublicOneTimeContractPage />} />
        {/* Signature request on an uploaded document. Distinct path from
            /sign/:token, which is the one-time-contract flow. */}
        <Route path="/sign-document/:token" element={<PublicSignDocumentPage />} />
        <Route path="/pay/:token" element={<PublicOneTimeContractPage />} />
        <Route path="/pay-confirm/:token" element={<PublicPaymentConfirmPage />} />
        <Route path="/pay-thanks/:contractId/:sessionId" element={<PublicPaymentSuccessPage />} />

        {/* Magic-link customer portal (no auth). /portal is the email
            entry form, /portal/:token is the cross-contract view. */}
        <Route path="/portal" element={<CustomerPortalRequestPage />} />
        <Route path="/portal/:token" element={<CustomerPortalPage />} />

        {/* Customer-side aggregated dashboard. Requires auth â€” gates the
            ACROSS-contracts view only; the individual contract is still
            reachable via /sign/:token without an account. */}
        <Route
          path="/dashboard/customer"
          element={
            <RequireAuth>
              <CustomerDashboardPage />
            </RequireAuth>
          }
        />

        {/* Authenticated standalone page (no shared layout) - time-entry-only roles */}
        {/* Uploaded signed contracts that name you, waiting for you to confirm
            them (contracts/upload_confirmation.py). Linked from the
            notification and the email. */}
        <Route
          path="/contracts/confirm"
          element={
            <RequireAuth>
              <ConfirmContractsPage />
            </RequireAuth>
          }
        />
        {/* Two-step sign-in on and off, backup codes. For every signed-in user. */}
        <Route
          path="/account/security"
          element={
            <RequireAuth>
              <AccountSecurityPage />
            </RequireAuth>
          }
        />
        <Route
          path="/clock"
          element={
            <RequireAuth>
              <ClockInPage />
            </RequireAuth>
          }
        />
        <Route
          path="/clock/credentials"
          element={
            <RequireAuth>
              <ClockInCredentialsPage />
            </RequireAuth>
          }
        />

        {/* ============================================================
            PUBLIC ROUTES - Marketing Site
            ============================================================ */}
        {/* Where Calendly redirects after a call is booked. Standalone (no
            marketing nav) so the one thing on screen is the calculator. */}
        <Route path="/calendly-confirmation" element={<CalendlyConfirmationPage />} />
        <Route path="/" element={<HeroLanding />}>
          <Route index element={<SmartHome />} />
          <Route path="for-talent" element={<TalentLanding />} />
          {/* Ad landing pages: same content, noindex, no audience tabs.
              Point employer ads at /employers and contractor ads at /contractors. */}
          <Route path="employers" element={<BusinessLanding adLP />} />
          <Route path="contractors" element={<TalentLanding adLP />} />
          <Route path="about" element={<About />} />
          <Route path="contact" element={<ContactPage />} />
          {/* The landing pages' secondary CTA. Same form, walkthrough framing. */}
          <Route path="walkthrough" element={<ContactPage />} />
          <Route path="blog" element={<BlogPage />} />
          <Route path="blog/:slug" element={<BlogPostPage />} />
          <Route path="partner/" element={<PartnerProgramPage />} />
          <Route path="partner/apply" element={<PartnerApplyPublicPage />} />
          <Route path="solutions/:slug" element={<SEOLandingPage />} />
          <Route path="orgs/:slug" element={<OrgStorefrontPage />} />
          <Route path="browse" element={<MarketHome surface="browse" />} />
          <Route path="services" element={<MarketHome surface="services" />} />
          <Route path="services/fractional" element={<MarketShell><ServicesFractionalCatalogPage /></MarketShell>} />
          <Route path="services/fractional/:slug" element={<MarketShell><ServicesFractionalRolePage /></MarketShell>} />
          <Route path="services/:vertical" element={<MarketShell><ServicesVerticalPage /></MarketShell>} />
          <Route path="services/:vertical/:offering" element={<MarketShell><ServicesOfferingPage /></MarketShell>} />
          <Route path="c/:slug" element={<ContractorPublicProfilePage />} />
          <Route path="requests/:token" element={<PublicRequestTrackerPage />} />
          <Route path="rate/:token" element={<PublicRateRequestPage />} />
          <Route path="postings" element={<MarketHome surface="postings" />} />
          <Route path="postings/:id" element={<PublicPostingDetailPage />} />
          <Route path="privacy" element={<PrivacyPolicyPage />} />
          <Route path="security" element={<SecurityPage />} />
          <Route path="calculator" element={<SavingsCalculatorPage />} />
          <Route path="cost-calculator" element={<SavingsCalculatorPage />} />
          <Route path="terms" element={<TermsPage />} />
          {/* The versioned agreement people actually accept, as opposed to the
              hand-written /terms page above. AgreementGate links here so the
              document somebody reads is the document their acceptance row
              points at. */}
          <Route path="legal/:slug" element={<LegalAgreementPage />} />
          <Route path="dmca" element={<DMCAPage />} />
          <Route path="accessibility" element={<AccessibilityPage />} />
          <Route path="account/delete" element={<DeleteAccountPage />} />
          <Route path="account/deleted" element={<AccountDeletedPage />} />
        </Route>

        {/* ============================================================
            AUTH ROUTES
            ============================================================ */}
        <Route path="/auth" element={<AuthLayout />}>
          <Route path="login" element={<LoginPage />} />
          <Route path="register" element={<RegisterPage />} />
          <Route path="organization/register" element={<OrganizationRegisterPage />} />
        </Route>

        {/* Stripe payment complete â€” auto-closes the tab */}
        <Route path="/payment-complete" element={<PaymentComplete />} />

        {/* Email verification */}
        <Route path="/verify-email/" element={<VerifyEmailPage />} />
        {/* The address in the verification email. Hands straight back to the
            API endpoint that does the verifying; it exists so the link a
            person reads says siftcareers.com rather than the azurewebsites
            API host. Must come after the bare route above so it does not
            swallow it. */}
        <Route path="/verify-email/:token" element={<VerifyEmailTokenPage />} />
        <Route path="/auth/verified" element={<EmailVerifiedPage />} />

        {/* Password reset flow */}
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/forgot-password/sent" element={<ForgotPasswordSentPage />} />
        <Route path="/reset/:uidb64/:token" element={<ResetPasswordPage />} />
        {/* A team invite: signs the person in and lands them on their home.
            See pages/InviteAcceptPage.jsx and accounts/views_invite.py. */}
        <Route path="/join/:uidb64/:token" element={<InviteAcceptPage />} />
        {/* An invite to somebody who already uses Sift: they choose to join (RT-AU08). */}
        <Route path="/join-team/:token" element={<JoinTeamPage />} />
        <Route path="/reset/done" element={<ResetDonePage />} />

        {/* Public profiles */}
        <Route path="/profile/contractor/:id" element={<ContractorProfilePage />} />
        <Route path="/profile/company/:id" element={<CompanyProfilePage />} />

        {/* Other public pages */}
        <Route path="/home" element={<HomePage />} />
        {/* Native first run. Standalone on purpose -- no marketing nav,
            no footer, nothing to browse past the one question. */}
        <Route path="/welcome-to-sift" element={<AppWelcome />} />

        {/* Where Facebook's redirect flow comes back to. Public, because the
            person is not authenticated until this screen stores the token it
            was handed. */}
        <Route path="/auth/social" element={<SocialAuthLanding />} />

        {/* Get-started teaser â€” every landing CTA routes here first (see BusinessLanding). */}
        <Route path="/get-started" element={<GetStartedPreview />} />

        {/* ============================================================
            PRIVATE ROUTES - Authenticated + ToS Gate
            ============================================================ */}
        <Route element={<PrivateShell />}>
          {/* Messages, both sides. Rebuilt around the post and the contract
              after the June archive; see pages/MessagesPage.jsx. */}
          <Route path="/messages" element={<MessagesPage />} />

          {/* The rest of signup, on the other side of the account.

              An individual's signup now ends the moment the account exists —
              the resume is still being read, and the questions about where and
              when they're available belong next to the posts they apply to.
              Both live here. Outside the dashboard layout on purpose: this is
              the tail of the flow they just started, not somewhere they
              navigated to, and it should look like it. */}
          <Route path="/welcome" element={<FinishSetupPage />} />

          {/* ========== COMPANY ROUTES ========== */}
          <Route path="/company" element= {<CompanyRoute><Outlet /></CompanyRoute>}>
            <Route path="dashboard" element={<CompanyDashboardPage />} />
            <Route path="workforce" element={<WorkforceCenterPage />} />

            {/* PROTECTED: Owner/Admin only */}

            <Route path="org-management" element={<ProtectedOrgManagementPage />} />
            <Route path="team" element={<ProtectedTeamPage />} />
            <Route path="finance/income" element={<ProtectedIncomeStatementPage />} />
            <Route path="connect-stripe" element={<ProtectedStripePage />} />
            <Route path="subscribe" element={<ProtectedBillingPage />} />
            <Route path="billing" element={<ProtectedBillingPage />} />
            {/* ARCHIVED 2026-06-11: Business Card retired. <Route path="card" element={<CompanyCardEditor />} /> */}
            <Route path="fraud" element={<ProtectedFraudAlertsPage />} />

            {/* Tiered access - component handles filtering */}

            <Route path="finance" element={<CompanyFinancePage />} />
            <Route path="contracts" element={<CompanyContractsPage />} />
            <Route path="internal-projects" element={<CompanyInternalProjectsPage />} />
            <Route path="internal-projects/:projectId" element={<CompanyInternalProjectDetailPage />} />
            <Route path="pay-runs" element={<CompanyPayRunsPage />} />
            <Route path="pay-runs/new" element={<CompanyPayRunPage />} />
            <Route path="pay-runs/:runId" element={<CompanyPayRunPage />} />
            <Route path="contracts/archive" element={<CompanyContractArchivePage />} />
            <Route path="calendar" element={<OrgTeamCalendarPage />} />
            <Route path="contracts/new" element={<NewContractFromTemplate />} />
            <Route path="hire" element={<CompanyHirePage />} />
            <Route path="onboarding" element={<CompanyOnboardingPage />} />
            <Route path="milestones" element={<CompanyMilestonesPage />} />
            <Route path="catalog" element={<CompanyCatalogPage />} />
            <Route path="transactions" element={<TransactionHistoryPage />} />
            <Route path="subscribe" element={<CompanySubscribePage />} />
            <Route path="billing" element={<CompanySubscribePage />} />
            {/* ARCHIVED 2026-06-11: Talent Search retired. <Route path="talent-search" element={<CompanyTalentSearchPage />} /> */}
            <Route path="timecards" element={<CompanyTimecardsPage />} />
            <Route path="documents" element={<CompanySignatureDocumentsPage />} />
            <Route path="notices" element={<CompanyNoticesPage />} />
            <Route path="agreements" element={<CompanyAgreementsPage />} />
            <Route path="partner-program" element={<CompanyPartnerProgramPage />} />
            <Route path="time-off" element={<CompanyTimeOffPage />} />
            {/* ARCHIVED 2026-06-11: Messaging Center retired. <Route path="messages" element={<CompanyMessagingCenterPage />} /> */}
            <Route path="inbound-requests" element={<CompanyInboundRequestsPage />} />
            <Route path="postings" element={<CompanyPostingsPage />} />
            <Route path="question-sets" element={<QuestionSetsPage />} />
            <Route path="postings/:id" element={<CompanyPostingBoardPage />} />
            <Route path="storefront" element={<CompanyStorefrontEditorPage />} />
            <Route path="card" element={<YourCardPage side="company" />} />
            <Route path="purchase-orders" element={<CompanyPOCenterPage />} />
            <Route path="credentials" element={<CompanyCredentialCenterPage />} />


            <Route path="contracts/:id/print" element={<CompanyContractPrintPage />}/>

            {/* Travel */}
            <Route path="travel-approvals" element={<CompanyTravelApprovalsPage />} />
            <Route path="travel" element={<CompanyTravelCenterPage />} />

            {/* Fraud management */}
            <Route path="fraud" element={<ProtectedFraudAlertsPage />} />
            <Route path="fraud/appeals" element={<ProtectedFraudAlertsPage />} />
            <Route path="fraud/trends" element={<ProtectedFraudAlertsPage />} />
            <Route path="fraud/disputes" element={<ProtectedFraudAlertsPage />} />
          </Route>

          {/* ========== PLATFORM ADMIN ========== */}
          {/* Seven modules, each with sections (components/admin/adminModules.js). */}
          <Route path="/admin" element={<AdminToday />} />
          <Route path="/admin/marketing" element={<LegacyMarketingRedirect />} />
          {Object.entries(LEGACY_REDIRECTS).map(([from, to]) => (
            <Route key={from} path={from} element={<Navigate to={to} replace />} />
          ))}
          <Route path="/admin/:module" element={<AdminModuleRedirect />} />
          <Route path="/admin/:module/:section" element={<AdminSection />} />

          {/* Contract signing (outside company namespace) */}
          <Route path="/contracts/:id/sign" element={<ContractSignPage />} />

          {/* ========== CONTRACTOR/USER DASHBOARD ROUTES ========== */}
          <Route path="/dashboard" element={<DashboardLayout />}>
            {/* Bare /dashboard is not a page â€” it routes to the dashboard this
                account actually has. See DashboardIndexRedirect. */}
            <Route index element={<DashboardIndexRedirect />} />

            {/* Cross-role: requests the user has sent through any storefront */}
            <Route path="my-requests" element={<MyRequestsPage />} />

            {/* Cross-role: notices an organisation broadcast to its roster.
                The emailed link and the bell notification both point here. */}
            <Route path="notices" element={<MyNoticesPage />} />

            {/* The contractor-side inbox for anything a company has sent them
                for signature -- not necessarily tied to a contract. Spans
                every organisation that has sent something, same as notices. */}
            <Route path="contractor/documents" element={<ContractorDocumentsToSignPage />} />

            {/* Contractor routes */}
            <Route path="contractor" element={<ContractorDashboard />} />
            <Route path="contractor/calendar" element={<JobCalendarPage />} />
            {/* ARCHIVED 2026-06-11: Messaging Center retired. <Route path="contractor/messages" element={<ContractorMessagingCenterPage />} /> */}
            <Route path="contractor/postings" element={<ContractorPostingsPage />} />
            <Route path="contractor/applications" element={<ApplicationReviewPage />} />
            <Route path="contractor/inbox" element={<ContractorInboxPage />} />
            <Route path="contractor/storefront" element={<ContractorStorefrontEditorPage />} />
            <Route path="contractor/timecard" element={<ContractorTimecardsPage />} />
            <Route path="contractor/accounting" element={<ContractorAccountingCenterPage />} />
            <Route path="contractor/taxes" element={<ContractorTaxCenterPage />} />
            <Route path="contractor/my-earnings" element={<MyEarnings />} />
            <Route path="contractor/compliance" element={<ComplianceUploadPage />} />
            <Route path="contractor/travel" element={<ContractorTravelCenterPage />} />
            <Route path="contractor/travel/status" element={<ContractorTravelStatusPage />} />
            <Route path="contractor/travel/request/new" element={<ContractorTravelRequestPage />} />
            <Route path="contractor/travel_request_confirmation" element={<TravelConfirmationPage />} />
            <Route path="contractor/contract-center" element={<ContractorContractCenterPage />} />
            <Route path="contractor/stripe" element={<ContractorStripeSetupPage />} />
            <Route path="contractor/profile" element={<ContractorProfileEdit />} />
            <Route path="support" element={<ContractorSupportBot />} />
            <Route path="contractor/support" element={<ContractorSupportBot />} />
            <Route path="contractor/credentials" element={<ContractorCredentialsPanel />} />
            <Route path="contractor/settings/calendar-sync" element={<CalendarSyncSettingsPage />} />
            <Route path="contractor/assignments" element={<MyAssignmentsPage />} />
            <Route path="contractor/milestones" element={<ContractorMilestonesPage />} />
            <Route path="contracts/:id/print" element={<ContractorContractPrintPage />}/>
            <Route path="partner" element={<PartnerDashboardPage />} />
            {/* "Your card" (2026-10-31): the homepage card, edited where it comes
                from. Not the retired BusinessCard editor, which kept its own copy. */}
            <Route path="contractor/card" element={<YourCardPage side="contractor" />} />

            {/* Personal services search â€” deactivated. Redirect any lingering
                links/bookmarks back to the dashboard. */}
            <Route path="search/services" element={<Navigate to="/dashboard/contractor" replace />} />
          </Route>
        </Route>

        {/* ============================================================
            404 FALLBACK
            ============================================================ */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
      </Suspense>
    </div>
    </BrandingProvider>
  );
}
